// Email (ADR 0024): the reps' mailboxes in the console's Microsoft 365 drawer. The company's
// settings, the approval in Microsoft, the do-not-email list and who connected a mailbox.
// Mirrors crates/api-types/src/email.rs.
import { queryOptions } from "@tanstack/react-query";
import type { MailboxAdminSettings } from "@vd/api-client";
import { API_BASE } from "../config";
import { api, buildQuery } from "./client";

export type RetentionDays = 0 | 1 | 7 | 30;
export type TeamBriefWriters = "admins" | "admins_managers";

/** The whole set the sheet edits; `PUT /v1/admin/email/settings` takes all of it. */
export interface EmailSettingsInput {
  microsoft_enabled: boolean;
  body_retention_days: RetentionDays;
  drafting_enabled: boolean;
  draft_min_talk_seconds?: number;
  /** Null for the company's summary model ("Same as summaries"). */
  draft_model?: string | null;
  team_brief_writers: TeamBriefWriters;
  odoo_log: boolean;
  odoo_include_message: boolean;
  company_address?: string | null;
  /** Domains for extra accounts and shared mailboxes (ADR 0037). Left out: unchanged. */
  mailbox_domains?: string[];
  /** Reps may add shared mailboxes (ADR 0037). Left out: unchanged. */
  allow_shared_by_reps?: boolean;
}

export interface AdminConsent {
  at: string;
  by_user_id?: string | null;
  by_name?: string | null;
  /** The customer's Microsoft tenant. */
  tid: string;
  scopes: string[];
}

export interface DraftModelOption {
  id: string;
  label: string;
  /** The company has the provider's key. */
  available: boolean;
}

export interface EmailAdmin {
  settings: EmailSettingsInput;
  microsoft_enabled_at?: string | null;
  microsoft_enabled_by_name?: string | null;
  /** Gmail is a later phase: always false. */
  gmail_enabled: boolean;
  admin_consent?: AdminConsent | null;
  /** False when the server has no Microsoft app for mail (approval can't start). */
  microsoft_configured: boolean;
  models: DraftModelOption[];
  dnc: { count: number; added_this_week: number };
  accounts: { connected: number; members: number };
  updated_at?: string | null;
  /** Mailbox domains, the Google setup and the Microsoft approval per scope group (ADR 0037). */
  mailboxes?: MailboxAdminSettings | null;
}

export interface AdminConsentStart {
  url: string;
  expires_at: string;
}

export type AccountStatus = "connected" | "needs_reconnect" | "not_connected";

export interface EmailAccountRow {
  user_id: string;
  name: string;
  /** The member's Kite sign-in. */
  email: string;
  /** The connected mailbox's address. */
  address?: string | null;
  provider?: string | null;
  status: AccountStatus;
  connected_at?: string | null;
  sent_this_month: number;
}

export type DncKind = "address" | "domain";
/** `unsubscribed`: the person used the Unsubscribe link in an email. */
export type DncReason = "asked" | "admin" | "bounced" | "unsubscribed";

export interface DncRow {
  id: string;
  kind: DncKind;
  /** Lowercased address, or a domain. */
  value: string;
  reason: DncReason;
  added_by_name?: string | null;
  created_at: string;
}

export interface DncPage {
  items: DncRow[];
  /** Matching the search. */
  total: number;
  added_this_week: number;
  next_cursor?: string | null;
}

export interface DncImportResult {
  added: number;
  already: number;
  invalid: number;
  /** The first lines without an address (1-based), up to 20. */
  invalid_lines: number[];
  /** The file had more than 10,000 rows; the rest were not read. */
  too_many: boolean;
}

export const emailKeys = {
  admin: ["admin", "email"] as const,
  accounts: ["admin", "email", "accounts"] as const,
  dncAll: ["admin", "email", "dnc"] as const,
  dnc: (q: string) => ["admin", "email", "dnc", q] as const,
};

export const emailAdminQuery = queryOptions({
  queryKey: emailKeys.admin,
  queryFn: () => api<EmailAdmin>("/v1/admin/email"),
});

export const emailAccountsQuery = queryOptions({
  queryKey: emailKeys.accounts,
  queryFn: () => api<{ items: EmailAccountRow[] }>("/v1/admin/email/accounts"),
});

export const DNC_PAGE = 50;

export const emailApi = {
  saveSettings: (body: EmailSettingsInput) =>
    api<EmailAdmin>("/v1/admin/email/settings", { method: "PUT", body }),
  startConsent: () => api<AdminConsentStart>("/v1/admin/email/admin-consent", { method: "POST" }),
  dnc: (q: string, cursor: string | null) =>
    api<DncPage>("/v1/admin/email/dnc", { query: { q, cursor, limit: DNC_PAGE } }),
  addDnc: (value: string) => api<DncRow>("/v1/admin/email/dnc", { method: "POST", body: { value } }),
  removeDnc: (id: string) => api<void>(`/v1/admin/email/dnc/${encodeURIComponent(id)}`, { method: "DELETE" }),
  importDnc: (csv: string) =>
    api<DncImportResult>("/v1/admin/email/dnc/import", {
      method: "POST",
      raw: { body: csv, type: "text/csv" },
    }),
};

/** The CSV export's address (opened with the console's cookie), narrowed like the search. */
export function dncCsvHref(q = ""): string {
  return `${API_BASE}/v1/admin/email/dnc${buildQuery({ format: "csv", q: q.trim() })}`;
}

export const RETENTION_OPTIONS: { value: `${RetentionDays}`; label: string }[] = [
  { value: "0", label: "Don't keep" },
  { value: "1", label: "1 day" },
  { value: "7", label: "7 days" },
  { value: "30", label: "30 days" },
];

export const BRIEF_WRITERS: { value: TeamBriefWriters; label: string }[] = [
  { value: "admins", label: "Admins" },
  { value: "admins_managers", label: "Admins and managers" },
];

export const DNC_REASON: Record<DncReason, string> = {
  asked: "Asked",
  admin: "Added by admin",
  bounced: "Bounced",
  unsubscribed: "Unsubscribed",
};

export const ACCOUNT_STATUS: Record<
  AccountStatus,
  { label: string; tone: "success" | "warning" | "outline" }
> = {
  connected: { label: "Connected", tone: "success" },
  needs_reconnect: { label: "Needs reconnect", tone: "warning" },
  not_connected: { label: "Not connected", tone: "outline" },
};

/** What the server redirects back with after Microsoft's approval page. */
export type ConsentOutcome =
  | "approved"
  | "declined"
  | "wrong_tenant"
  | "no_entra_tenant"
  | "not_admin"
  | "expired";

export const CONSENT_TOAST: Record<ConsentOutcome, { text: string; tone: "ok" | "info" | "danger" }> = {
  approved: { text: "Approved for everyone.", tone: "ok" },
  declined: { text: "Approval was declined in Microsoft.", tone: "info" },
  wrong_tenant: { text: "Approve with your company's Microsoft account.", tone: "danger" },
  no_entra_tenant: {
    text: "Kite doesn't know your company's Microsoft tenant yet. Ask Vertec support to add it.",
    tone: "danger",
  },
  not_admin: { text: "Only a Kite admin can approve email for everyone.", tone: "danger" },
  expired: { text: "That approval link expired. Try again.", tone: "danger" },
};

/** "Oct 22". */
export function monthDay(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The import result in words. */
export function importResultText(r: DncImportResult): string {
  const parts = [`Added ${r.added}.`];
  if (r.already > 0)
    parts.push(r.already === 1 ? "1 was already on the list." : `${r.already} were already on the list.`);
  if (r.invalid > 0) {
    const lines =
      r.invalid_lines.length > 0
        ? ` (${r.invalid === 1 ? "line" : "lines"} ${r.invalid_lines.join(", ")}${r.invalid > r.invalid_lines.length ? ", and more" : ""})`
        : "";
    parts.push(
      r.invalid === 1 ? `1 line had no address${lines}.` : `${r.invalid} lines had no address${lines}.`,
    );
  }
  if (r.too_many) parts.push("Only the first 10,000 rows were read.");
  return parts.join(" ");
}

/** A domain row reads "@example.com". */
export function dncLabel(r: Pick<DncRow, "kind" | "value">): string {
  return r.kind === "domain" && !r.value.startsWith("@") ? `@${r.value}` : r.value;
}
